import { DataVariants } from "../../../data/DataManager";
import { InputTypes } from "../../../form/FormManager";
import { EMPTY_VALUE, FieldValue, FieldSchema, isEmpty } from "../../../Schema";
import { LinkedInstance } from "../external-sources/LinkedInstances";
import { User } from "../external-sources/Users";
import { Modes } from "./mode";

/** The affirmative spellings a record has been written with over the years. */
const RELEVANT = ["yes", "true"];

/**
 * Reads a stored value as one of the two options.
 *
 * The field is a Yes/No question, but the records do not all say so. The GraphQL enum also
 * admits `NOTAVAILABLE`, and across the environments the attribute holds `"Yes"`, `"No"`,
 * `"NOTAVAILABLE"`, `"Research Use Only"`, a DynamoDB null, or nothing at all. An enum only
 * keeps a value that matches one of its options, so every spelling outside the two was
 * being reduced to empty — which on a required field means a record nobody can edit without
 * first answering a question about it that was already answered.
 *
 * So anything affirmative reads as "Yes", and any other value that is actually there reads
 * as "No": not GxP relevant is the safe reading, since only "Yes" pulls an edit into the
 * four-eyes review. An absent value stays absent, because that genuinely has not been
 * answered and the required rule should say so.
 *
 * @param value - The stored value.
 * @returns "Yes", "No", or EMPTY_VALUE when there is nothing stored.
 */
const toGxpOption = (value: FieldValue): FieldValue => {
  if (isEmpty(value)) {
    return EMPTY_VALUE;
  }
  return RELEVANT.includes(String(value).trim().toLowerCase()) ? "Yes" : "No";
};

export const gxpRelevant: FieldSchema = {
  name: "gxpRelevant",
  grid: {
    displayed: true,
    order: 24,
  },
  // Only the inbound directions. A CSV cell is deliberately left alone: an import that
  // quietly turned an unrecognised answer into "No" would be accepting a bad file rather
  // than reporting it, and the enum check already tells the uploader which row to fix.
  dataTransform: {
    fromGraphql: {
      getter: ({ gxpRelevant }) => toGxpOption(gxpRelevant),
    },
    fromDb: {
      getter: ({ gxpRelevant }) => toGxpOption(gxpRelevant),
    },
  },
  data: {
    variant: DataVariants.ENUM,
    options: [{ label: "No", value: "No" }, { label: "Yes", value: "Yes" }]
  },
  display: {
    displayed: true,
  },
  validation: { required: true },
  form: {
    label: "Intended use GxP relevant?",
    type: InputTypes.SELECT,
    disabled: {
      getter: ({ linkedInstance, mode, currentUser }) => {
        return (
          [Modes.EDIT, Modes.BULK_EDIT].includes(mode as Modes) &&
          ["P1S Uni-Directional"].includes((linkedInstance as LinkedInstance)?.linkedInstance)
        ) && !(currentUser as User)?.isAdmin;
      },
      fields: ["linkedInstance", "mode", "currentUser"],
    },
  },
};
